/* ──────────────────────────────────────────────
 *  Suppléments d'une ligne : sélection + quantités choisies
 *
 *  Un supplément retenu SANS quantité suit la règle historique
 *  (une unité par unité facturée de la ligne, cf.
 *  `resolveSupplementQuantity`). Une quantité n'existe qu'une fois
 *  le stepper touché, et seulement pour un supplément qui se compte
 *  au choix (`quantity_selectable`). Point de passage unique entre
 *  l'état du tunnel et les payloads / stockages, pour que panier,
 *  pré-réservation et persistance envoient la même chose.
 * ────────────────────────────────────────────── */

import { resolveSupplementQuantityMax, type SupplementQuantity } from '@cfixe-monorepo/shared';
import type { BookingCallbacks, CartItem } from '../types';

/** Forme minimale d'un supplément de ligne (tunnel de réservation, carte cadeau). */
export interface QuantifiableSupplement {
  supplement_id: number;
  selected: boolean;
  quantity_selectable?: boolean;
  /** Quantité choisie au stepper ; absente tant que le client n'y a pas touché. */
  quantity?: number;
}

/** Ligne dont on lit les suppléments : quantité de prestations et taille de groupe fixent le plafond. */
interface SupplementLine<T extends QuantifiableSupplement> {
  supplements: readonly T[];
  quantity?: number | null;
  party_size?: number | null;
}

/** Suppléments d'une ligne tels que les API les attendent (panier, pré-réservation, carte cadeau). */
export interface SupplementSelectionPayload {
  selected_supplement_ids: number[];
  /** Quantités choisies des suppléments retenus ; absent quand aucune n'a été choisie. */
  supplement_quantities?: SupplementQuantity[];
}

/** Quantité exploitable bornée à [1, max], ou `undefined` (valeur absente, forgée ou périmée). */
const clampQuantity = (value: unknown, max: number): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(Math.max(Math.trunc(value), 1), max) : undefined;

/**
 * Ids des suppléments retenus + quantités choisies, seulement pour les suppléments retenus qui se
 * comptent au choix. Un supplément décoché ou sans quantité n'envoie rien : le backend applique la
 * règle historique.
 */
export function toSupplementSelectionPayload<T extends QuantifiableSupplement>(
  line: SupplementLine<T>,
): SupplementSelectionPayload {
  const selected = line.supplements.filter((s): boolean => s.selected);
  const max = resolveSupplementQuantityMax(line);
  const quantities = selected.flatMap((s): SupplementQuantity[] => {
    const quantity = s.quantity_selectable === true ? clampQuantity(s.quantity, max) : undefined;
    return quantity === undefined ? [] : [{ supplement_id: s.supplement_id, quantity }];
  });
  return {
    selected_supplement_ids: selected.map((s): number => s.supplement_id),
    ...(quantities.length > 0 ? { supplement_quantities: quantities } : {}),
  };
}

/**
 * Reconstruit les suppléments d'une ligne depuis le catalogue VIVANT et des choix sérialisés
 * (panier persisté, panier d'un site vitrine). Ces choix viennent de localStorage : une quantité
 * n'est reprise que pour un supplément retenu qui se compte encore au choix, et re-bornée au
 * plafond de la ligne.
 */
export function buildSelectedSupplements<T extends { supplement_id: number; quantity_selectable?: boolean }>(
  catalog: readonly T[] | undefined,
  selection: {
    selected_supplement_ids?: readonly number[] | null;
    supplement_quantities?: readonly SupplementQuantity[] | null;
    quantity?: number | null;
    party_size?: number | null;
  },
): (T & { selected: boolean; quantity?: number })[] {
  const selectedIds = new Set(selection.selected_supplement_ids ?? []);
  const max = resolveSupplementQuantityMax(selection);
  const quantities = Array.isArray(selection.supplement_quantities) ? selection.supplement_quantities : [];
  return (catalog ?? []).map((s): T & { selected: boolean; quantity?: number } => {
    const selected = selectedIds.has(s.supplement_id);
    const chosen =
      selected && s.quantity_selectable === true
        ? clampQuantity(quantities.find((entry): boolean => entry?.supplement_id === s.supplement_id)?.quantity, max)
        : undefined;
    return { ...s, selected, ...(chosen !== undefined ? { quantity: chosen } : {}) };
  });
}

/** Ligne envoyée au récapitulatif du panier (`onComputeCart`). */
export type CartSummaryItemPayload = Parameters<BookingCallbacks['onComputeCart']>[0][number];

/** Ligne du panier → ligne du récapitulatif : même payload pour le récap live et celui du paiement. */
export const toCartSummaryItemPayload = (item: CartItem): CartSummaryItemPayload => ({
  prestation_id: item.prestation.prestation_id,
  quantity: item.quantity,
  selected_price_index: item.selectedPriceIndex,
  ...toSupplementSelectionPayload(item),
  party_size: item.party_size,
});
